import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
};

const DEFAULT_COLLECTIONS = [
  {
    id: 'petwear',
    title: 'Petwear',
    subtitle: 'For Your Best Friend',
    description: 'Stylish sweaters, vests, and more designed for comfort and fashion.',
    image: 'https://images.unsplash.com/photo-1766781497248-bf078aa029c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxjdXRlJTIwZG9nJTIwd2VhcmluZyUyMHN3ZWF0ZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzIyMjQwMDR8MA&ixlib=rb-4.1.0&q=85',
    accent: '#c68b4d',
  },
  {
    id: 'peoplewear',
    title: 'People Wear',
    subtitle: 'For You',
    description: 'Comfortable hoodies, tees, and accessories that complement your pup.',
    image: 'https://images.unsplash.com/photo-1608368266230-a0aaede93158?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxkb2clMjBvd25lciUyMG1hdGNoaW5nJTIwb3V0Zml0JTIwc3dlYXRlciUyMGZhc2hpb258ZW58MHx8fHwxNzcyMjI0MDAzfDA&ixlib=rb-4.1.0&q=85',
    accent: '#1e3a5f',
  },
  {
    id: 'matching',
    title: 'Matching Sets',
    subtitle: 'Best Friends Bundle',
    description: 'The perfect pairing for you and your furry companion.',
    image: 'https://images.unsplash.com/photo-1765934984444-fdb8dbe062b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxjdXRlJTIwZG9nJTIwd2VhcmluZyUyMHN3ZWF0ZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzIyMjQwMDR8MA&ixlib=rb-4.1.0&q=85',
    accent: '#e76f51',
  },
];

export const FeaturedCollections = () => {
  const [collections, setCollections] = useState(DEFAULT_COLLECTIONS);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        const settings = response.data;
        
        // Update collection images if they exist in settings
        setCollections(prev => prev.map(collection => {
          if (collection.id === 'petwear' && settings?.collection_petwear_image) {
            return { ...collection, image: getImageUrl(settings.collection_petwear_image) };
          }
          if (collection.id === 'peoplewear' && settings?.collection_peoplewear_image) {
            return { ...collection, image: getImageUrl(settings.collection_peoplewear_image) };
          }
          if (collection.id === 'matching' && settings?.collection_matching_image) {
            return { ...collection, image: getImageUrl(settings.collection_matching_image) };
          }
          return collection;
        }));
      } catch (error) {
        console.log('Using default collection images');
      }
    };
    fetchSettings();
  }, []);

  return (
    <section
      data-testid="featured-collections"
      className="py-24 bg-[#f5f0e6]"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-left mb-16">
          <span className="font-accent text-2xl text-[#c68b4d] block mb-2">Shop by Collection</span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#1e3a5f]">
            The Cozy Edit
          </h2>
        </div>

        {/* Collections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {collections.map((collection, index) => (
            <Link
              key={collection.id}
              to={`/shop?category=${collection.id}`}
              data-testid={`collection-card-${collection.id}`}
              className="group relative overflow-hidden rounded-3xl bg-white shadow-sm hover:shadow-xl transition-all duration-500"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Image Container */}
              <div className="relative h-[400px] overflow-hidden">
                <img
                  src={collection.image}
                  alt={collection.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/80 via-[#1e3a5f]/20 to-transparent" />
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <span
                    className="font-body text-sm font-medium tracking-wide uppercase opacity-80"
                    style={{ color: collection.accent }}
                  >
                    {collection.subtitle}
                  </span>
                  <h3 className="font-heading text-2xl md:text-3xl font-medium mt-1 mb-2">
                    {collection.title}
                  </h3>
                  <p className="font-body text-sm leading-relaxed opacity-80 mb-4">
                    {collection.description}
                  </p>
                  
                  {/* CTA */}
                  <div className="flex items-center gap-2 font-body text-sm font-semibold opacity-0 transform translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                    <span>Explore Collection</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedCollections;
